import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Friendship graph hooks (migration_116). All reads go through the
// friends_overview / search_user SECURITY DEFINER RPCs so
// user_profiles itself stays self-read-only — a friend's name and
// avatar are the only fields that ever cross accounts.

export function useFriends() {
  const { user } = useAuth()
  const userId = user?.id || null
  const [rows, setRows] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refetch = useCallback(async () => {
    if (!userId) return
    const [overview, suggested] = await Promise.all([
      supabase.rpc('friends_overview'),
      supabase.rpc('suggested_friends', { max_results: 8 }),
    ])
    if (overview.error) { setError(overview.error.message); return }
    setError(null)
    setRows(overview.data || [])
    // Suggestions are best-effort — a failure (e.g. migration not yet
    // applied) just leaves the section empty.
    if (!suggested.error) setSuggestions(suggested.data || [])
  }, [userId])

  useEffect(() => {
    if (!userId) { setRows([]); setLoading(false); return undefined }
    let alive = true
    setLoading(true)
    const channel = supabase
      .channel(`friendships-${userId}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'friendships' },
        () => { if (alive) refetch() },
      )
      .subscribe()
    refetch().then(() => { if (alive) setLoading(false) })
    return () => { alive = false; supabase.removeChannel(channel) }
  }, [userId, refetch])

  const friends = useMemo(() => rows.filter((r) => r.status === 'accepted'), [rows])
  const incoming = useMemo(() => rows.filter((r) => r.status === 'pending' && r.direction === 'incoming'), [rows])
  const outgoing = useMemo(() => rows.filter((r) => r.status === 'pending' && r.direction === 'outgoing'), [rows])
  // friends_overview only returns blocks I placed — the blocked side
  // never sees the row at all.
  const blocked = useMemo(() => rows.filter((r) => r.status === 'blocked'), [rows])

  // Exact-match handle lookup. Returns the profile row or null.
  const search = useCallback(async (handle) => {
    const { data, error: err } = await supabase.rpc('search_user', { handle })
    if (err) throw new Error(err.message)
    return data?.[0] || null
  }, [])

  const sendRequest = useCallback(async (otherUserId) => {
    const { error: err } = await supabase
      .from('friendships')
      .insert({ requester_id: userId, addressee_id: otherUserId })
    if (err) {
      // 23505 = the pair already has a row (pending/accepted/blocked).
      setError(err.code === '23505' ? null : err.message)
      return false
    }
    // Fire-and-forget — a failed push must not fail the request.
    supabase.functions.invoke('friends-push', { body: { kind: 'request', to: otherUserId } }).catch(() => {})
    await refetch()
    return true
  }, [userId, refetch])

  const accept = useCallback(async (row) => {
    const { error: err } = await supabase
      .from('friendships')
      .update({ status: 'accepted' })
      .eq('id', row.friendship_id)
    if (err) { setError(err.message); return false }
    supabase.functions.invoke('friends-push', { body: { kind: 'accepted', to: row.other_user_id } }).catch(() => {})
    await refetch()
    return true
  }, [refetch])

  // Decline / cancel / unfriend / unblock are all the same operation:
  // drop the row so a fresh request is possible afterwards.
  const remove = useCallback(async (row) => {
    const { error: err } = await supabase
      .from('friendships')
      .delete()
      .eq('id', row.friendship_id)
    if (err) { setError(err.message); return false }
    await refetch()
    return true
  }, [refetch])

  const block = useCallback(async (row) => {
    const { error: err } = await supabase
      .from('friendships')
      .update({ status: 'blocked' })
      .eq('id', row.friendship_id)
    if (err) { setError(err.message); return false }
    await refetch()
    return true
  }, [refetch])

  return { friends, incoming, outgoing, blocked, suggestions, loading, error, search, sendRequest, accept, remove, block, refetch }
}

const HANDLE_RE = /^[a-z0-9_]{3,20}$/

// The caller's own public identity (@handle + display name).
export function useMyHandle() {
  const { user } = useAuth()
  const userId = user?.id || null
  const [identity, setIdentity] = useState({ username: '', display_name: '' })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) { setLoading(false); return undefined }
    let alive = true
    supabase
      .from('user_profiles')
      .select('username, display_name')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return
        setIdentity({ username: data?.username || '', display_name: data?.display_name || '' })
        setLoading(false)
      })
    return () => { alive = false }
  }, [userId])

  // Returns null on success or an error key the page can translate.
  const save = useCallback(async (username, displayName) => {
    const handle = (username || '').trim().toLowerCase()
    if (!HANDLE_RE.test(handle)) return 'invalid'
    const { error: err } = await supabase
      .from('user_profiles')
      .upsert(
        { user_id: userId, username: handle, display_name: (displayName || '').trim() || null },
        { onConflict: 'user_id' },
      )
    if (err) return err.code === '23505' ? 'taken' : err.message
    setIdentity({ username: handle, display_name: (displayName || '').trim() })
    return null
  }, [userId])

  return { identity, loading, save }
}
